import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MatTableDataSource } from '@angular/material/table';
import { getNamedEnum } from 'src/app/_helpers/enum-helper';
import { getHistoryChangedFields } from 'src/app/_helpers/history-changed-fields.helper';
import { NoiseFrequencyRating } from 'src/app/enums/noiseFrequencyRating.enum';
import { NoiseSpeedType } from 'src/app/enums/noiseSpeedType.enum';
import { getOelCorrectionMethodNames } from 'src/app/enums/oelCorrectionMethod.enum';
import { SampleTypeEnum } from 'src/app/enums/sampleType.enum';
import { HAZARD_CATEGORY_DISPLAY_LABELS } from 'src/app/enums/hazardCategory.enum';
import { TWACorrectionMethod } from 'src/app/enums/TWACorrectionMethod.enum';
import { UnitSymbols } from 'src/app/enums/units.enum';
import { AgentJurisdictionDialogComponent, IAgentJurisdictionData } from 'src/app/modals/agent-jurisdiction-dialog/agent-jurisdiction-dialog.component';
import { AgentJurisdictionHistoryDialogComponent } from 'src/app/modals/agent-jurisdiction-history-dialog/agent-jurisdiction-history-dialog.component';
import { AgentJurisdiction } from 'src/app/models/agent-jurisdiction.model';
import { Agent, AgentRecordHistory } from 'src/app/models/agent.model';
import { AgentOrgLevel } from 'src/app/models/agentOrglevel.model';
import { AgentJurisdictionService } from 'src/app/services/agent-jurisdiction.service';
import { AgentOrgLevelService } from 'src/app/services/agent-org-level.service';
import { AgentService } from 'src/app/services/agent.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { ToastService } from 'src/app/services/toast.service';
import { ValueConversionService } from 'src/app/services/value-conversion.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { FLYOUT_DATA } from 'src/app/shared/flyout/flyout-container/flyout-data.token';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { buildHistoryParams, getHistoryDateTimeDisplay, loadPopupHistory } from 'src/app/_helpers/history-popup.helper';
import { decorateAgentJurisdictionVersions } from 'src/app/_helpers/oel-version.helper';
import { TranslateService } from 'src/app/services/translate.service';
import { Subject, takeUntil } from 'rxjs';
import { GridPermissions } from 'src/app/shared/grid/core-grid/gridPermissions.enum';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';

@Component({
  standalone: false,
  selector: 'app-agent-details-popup',
  templateUrl: './agent-details-popup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './agent-details-popup.component.scss'
})
export class AgentDetailsPopupComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;

  agentDetails!: Agent;
  displayedColumns: string[] = [];
  displayedAssociatedColumns: string[] = [];
  associationsDataSource = new MatTableDataSource<AgentOrgLevel>();
  dataSource = new MatTableDataSource<AgentJurisdiction>();
  historyDisplayedColumns: string[] = [];
  historyDataSource = new MatTableDataSource<AgentRecordHistory>();
  oelCorrectionMethodEnum = getOelCorrectionMethodNames();
  twaCorrectionMethodEnum = getNamedEnum(TWACorrectionMethod);
  noiseFrequencyRatingEnum = NoiseFrequencyRating;
  noiseSpeedEnum = NoiseSpeedType;
  readonly permissionObject = RolePermissionObjects.AgentJurisdiction;
  readonly createAction = GridPermissions.Create;
  readonly updateAction = GridPermissions.Update;
  readonly deleteAction = GridPermissions.Delete;
  private readonly destroy$ = new Subject<void>();
  private historyLoaded = false;
  
  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  isLoading = false;
  isHistoryLoading = false;

  /** i18n key for the agent's Hazard Category label, or '' when unset. Pre-computed when agentDetails arrives. */
  hazardCategoryLabelKey = '';

  constructor(
    @Inject(FLYOUT_DATA) public data: string,
    private agentService: AgentService,
    private agentJurisdictionService: AgentJurisdictionService,
    private agentOrgLevelService: AgentOrgLevelService,
    private organizationLevelService: OrganizationLevelService,
    private toastService: ToastService,
    protected dialog: MatDialog,
    private valueConversionService: ValueConversionService,
    private dateFormatService: DateFormatService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    if (this.data) {
      this.getAgentDetails();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getAgentDetails() {
    this.isLoading = true;
    this.agentService.getById(this.data).subscribe({
      next: (response) => {
        this.agentDetails = response;
        const cat = response.hazardCategory;
        this.hazardCategoryLabelKey = cat != null ? HAZARD_CATEGORY_DISPLAY_LABELS[cat] : '';
        this.getOrgLevelParentId();
        this.setColumns();
        this.getJurisdictions();
      },
      error: (error) => {
        this.toastService.showError(error)
        this.isLoading = false;
      }
    });
  }

  setColumns() {
    const noiseFields = [
      nameOf<AgentJurisdiction>(x => x.ceilingLimit),
      nameOf<AgentJurisdiction>(x => x.stel),
      nameOf<AgentJurisdiction>(x => x.exchangeRate),
      nameOf<AgentJurisdiction>(x => x.threshold),
      nameOf<AgentJurisdiction>(x => x.noiseFrequencyRating),
      nameOf<AgentJurisdiction>(x => x.noiseSpeed),
    ]
    const otherFields = [
      nameOf<AgentJurisdiction>(x => x.oelCorrectionMethod),
      nameOf<AgentJurisdiction>(x => x.twaCorrectionMethod),
    ]
    this.displayedColumns = [
      nameOf<AgentJurisdiction>(x => x.jurisdictionName),
      nameOf<AgentJurisdiction>(x => x.al),
      nameOf<AgentJurisdiction>(x => x.oel),
      nameOf<AgentJurisdiction>(x => x.oelTypeName),
      ...(this.agentDetails.agentType === SampleTypeEnum.Noise ? noiseFields : otherFields),
      'effectivePeriod',
      'status',
      'actions'
    ];
    this.displayedAssociatedColumns = [
      nameOf<AgentOrgLevel>(x => x.organizationLevelName),
    ];
    this.historyDisplayedColumns = [
      nameOf<AgentRecordHistory>(x => x.name),
      nameOf<AgentRecordHistory>(x => x.casId),
      nameOf<AgentRecordHistory>(x => x.agentForm),
      nameOf<AgentRecordHistory>(x => x.agentType),
      nameOf<AgentRecordHistory>(x => x.healthAdvisory),
      nameOf<AgentRecordHistory>(x => x.consequenceRating),
      nameOf<AgentRecordHistory>(x => x.organizationLevelName),
      nameOf<AgentRecordHistory>(x => x.dateUpdated),
      nameOf<AgentRecordHistory>(x => x.updatedByUserName)
    ];
  }

  getHistory(): void {
    this.isHistoryLoading = true;
    const params = buildHistoryParams('agentId', this.data);

    loadPopupHistory(
      this.agentService.getHistory(params),
      this.destroy$,
      (history) => this.mapHistoryRecord(history),
      (records) => {
        this.historyDataSource.data = records;
        this.historyLoaded = true;
        this.isHistoryLoading = false;
      },
      (error) => {
        this.toastService.showError(error);
        this.historyDataSource.data = [];
        this.historyLoaded = false;
        this.isHistoryLoading = false;
      },
    );
  }

  onTabChange(event: MatTabChangeEvent): void {
    if (event.index === 2 && !this.historyLoaded) {
      this.getHistory();
    }
  }

  private mapHistoryRecord(history: AgentRecordHistory): AgentRecordHistory {
    return {
      ...history,
      changedFieldSet: new Set(getHistoryChangedFields(history)),
      dateUpdatedDisplay: getHistoryDateTimeDisplay(history.dateUpdated, this.dateFormatService),
      agentTypeDisplay: history.agentType == null ? '-' : (SampleTypeEnum[history.agentType] || '-'),
    };
  }

  getJurisdictions() {
    this.isLoading = true;
    this.agentJurisdictionService.getAgentDetails(this.agentDetails.id)
      .subscribe({
        next: (response) => {
        const decorated = decorateAgentJurisdictionVersions(response, this.dateFormatService, this.translateService);
        this.dataSource.data = decorated.map(res => ({ ...res, oelValue: this.getOelValue(res) }));
        this.isLoading = false;
      },
        error: (error) => {
          this.toastService.showError(error);
          this.dataSource.data = [];
          this.isLoading = false;
        }
      });
  }

  getOelValue(agent: AgentJurisdiction) {
    if (agent.oel == null) return '-';
    const agentUnit = agent.agentUnits;
    const hasUnit = agentUnit != null;
    const val = agentUnit != null
      ? this.valueConversionService.getParameterValueAsPerAgentUnit(agent.oel, agentUnit)
      : agent.oel;
    return `${+val.toFixed(4)}${hasUnit ? ' ' + UnitSymbols[agentUnit] : ''}`;
  }

  getOrgLevelParentId() {
    this.isLoading = true;
    this.organizationLevelService.getParentSite(this.agentDetails.organizationLevelId).subscribe({
      next: (response) => {
        if(!response){
          this.isLoading = false;
          return;
        }
        const { parentId, orgLevelType } = response;
        if (parentId == null) {
          this.isLoading = false;
          return;
        }
        this.getAgentOrgLabels(this.data, parentId, orgLevelType);
      },
      error: (error) => {
        this.toastService.showError(error);
        this.isLoading = false;
      }
    });

  }

  getAgentOrgLabels(agentId: string, parentOrgLevelId: string, orgLevelType?: number): void {
    this.isLoading = true;
    this.agentOrgLevelService.getAssociations(agentId, parentOrgLevelId, orgLevelType).subscribe({
      next: (response) => {
        this.associationsDataSource.data = response;
        this.isLoading = false;
      },
      error: (error) => {
        this.toastService.showError(error);
        this.isLoading = false;
      }
    });
  }
  openAgentJurisdictionDialog(entityId?: string, isReadonly = false) {
    const data: IAgentJurisdictionData = {
      id: entityId ?? '',
      masterAgentId: this.agentDetails.masterAgentId,
      isReadonly
    }
    const dialogRef = this.dialog.open(AgentJurisdictionDialogComponent, {
      data: data,
      width: '760px',
      disableClose: true
    });

    this.closeDialog(dialogRef);
  }

  openVersionHistory(entityId: string) {
    this.dialog.open(AgentJurisdictionHistoryDialogComponent, {
      data: entityId,
      width: '900px',
    });
  }

  openNewVersionDialog(entityId: string) {
    const data: IAgentJurisdictionData = {
      id: entityId,
      masterAgentId: this.agentDetails.masterAgentId,
      isNewVersion: true
    }
    const dialogRef = this.dialog.open(AgentJurisdictionDialogComponent, {
      data: data,
      width: '760px',
      disableClose: true
    });

    this.closeDialog(dialogRef);
  }

  openAgentJurisdictionDeleteDialog(id?: string) {
    // Guard: historical (closed) OEL versions must not be mutated/deleted.
    if (this.dataSource.data.find(r => r.id === id)?.isHistorical) {
      return;
    }
    const dialogRef = this.dialog.open(DeleteDialog, {
      data: {
        id: id,
        formDeletes: true,
        service: this.agentJurisdictionService,
        title: 'Sample'
      },
      width: '440px',
      disableClose: true
    });

    this.closeDialog(dialogRef);
  }

  closeDialog(dialogRef: MatDialogRef<unknown, unknown>): void {
    dialogRef.afterClosed().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result: unknown) => {
        if (result) { this.getJurisdictions() }
      }
    });
  }
}